import React, { useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Container, MenuItem, Select, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TimeSpentPanel from "bundles/Internal/components/common/TimeSpentPanel";
import DateRangeSelect from "bundles/Internal/components/common/DateRangeSelect";

const PERIODS = ["daily", "weekly", "monthly", "custom", "total"];

const TimeProjectShowPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/time-project"));

  const [period, setPeriod] = useState("total");
  const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });
  const [internalUserId, setInternalUserId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [contributors, setContributors] = useState([]);

  const handleLoaded = useCallback(
    (data) => {
      const project = data?.[0];
      if (!project) return;
      setProjectName(project.name || "");
      if (!internalUserId) setContributors(project.users || []);
    },
    [internalUserId],
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ py: 1.5 }}>
          <Button
            onClick={goBack}
            disableRipple
            sx={{
              minWidth: 0,
              p: 0.75,
              color: "hsl(150 12% 38%)",
              "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: -0.2,
              color: "hsl(150 30% 12%)",
              lineHeight: 1.2,
              fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            }}
          >
            {projectName || "Time project"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <Select
              value={internalUserId}
              onChange={(e) => setInternalUserId(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 160,
                backgroundColor: "#fff",
                fontSize: 12,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                All members
              </MenuItem>
              {contributors.map((u) => (
                <MenuItem key={u.id} value={String(u.id)} sx={{ fontSize: 12 }}>
                  {u.full_name || "Unknown"}
                </MenuItem>
              ))}
            </Select>

            {period === "custom" && (
              <DateRangeSelect
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(range) => setCustomRange(range)}
              />
            )}

            <Box
              sx={{
                display: "flex",
                backgroundColor: "hsl(140 6% 90%)",
                borderRadius: "8px",
                p: "2px",
                border: "1px solid hsl(140 8% 88%)",
              }}
            >
              {PERIODS.map((p) => (
                <Box
                  key={p}
                  component="button"
                  onClick={() => setPeriod(p)}
                  sx={{
                    all: "unset",
                    px: "16px",
                    py: "6px",
                    fontSize: 12,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 150ms ease",
                    color: period === p ? "hsl(150 30% 12%)" : "hsl(150 12% 38%)",
                    backgroundColor: period === p ? "#ffffff" : "transparent",
                    boxShadow: period === p ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    "&:hover": { color: "hsl(150 30% 12%)" },
                  }}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <TimeSpentPanel
          timeProjectId={id}
          period={period}
          customRange={customRange}
          internalUserId={internalUserId || undefined}
          onLoaded={handleLoaded}
        />
      </Container>
    </Box>
  );
};

export default TimeProjectShowPage;
